import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Box from '../shared/atoms/Box';
import Text from '../shared/atoms/Text';
import { RJ } from './rapportTokens';

// Status badge palette — brand pastels so the espace-client diagnostic matches
// the public report's row palette (RapportDiagnosticRow) and the brand charter
// (ticket #37 §6): no more red/yellow, marine text on pastel backgrounds.
//   ok          → green pastel  (item validated)
//   to_monitor  → blue  pastel  #ddedff (item to keep an eye on)
//   to_repair   → orange pastel #ffdfcf (item needs intervention)
//   null/other  → neutral gray
const badgePalette = (theme, status) => {
  const c = theme.colors;
  switch (status) {
    case 'ok':
      return { bg: c.green.pale, color: c.green.dark, border: c.green.lighter };
    case 'to_monitor':
      return { bg: RJ.bluePastel, color: RJ.text, border: RJ.bluePastel };
    case 'to_repair':
      return { bg: RJ.orangePastel, color: RJ.text, border: RJ.orangePastel };
    default:
      return {
        bg: c.gray.lightest,
        color: c.gray.darker,
        border: c.gray.light,
      };
  }
};

// Mirror of the API's CourseVeloElement::mapStatusFromValue (ADR-0003). The
// diagnostic scale is HIGH = healthy: the raw element_value posted by rl-staff
// is a numeric string 1..5 (5 = neuf → OK, 1 = hors d'usage → à réparer) or
// "NA":
//   "5" / "4"  → ok          "3"        → to_monitor
//   "2" / "1"  → to_repair         "NA" → no badge (not applicable)
// We also accept the enum names directly so Jest/Cypress fixtures and any
// future API enum un-wrap keep rendering.
export const mapElementValueToStatus = (value) => {
  if (value == null || value === '' || value === 'NA') return null;
  const str = String(value);
  if (str === '1' || str === '2') return 'to_repair';
  if (str === '3') return 'to_monitor';
  if (str === '4' || str === '5') return 'ok';
  if (str === 'ok' || str === 'to_monitor' || str === 'to_repair') return str;
  return null;
};

// Humanize "CHAIN_CONDITION" → "Chain condition" as a last-resort fallback
// when the i18n key `rapport.diagnostic.type.<KEY>` is missing.
const humanizeElementType = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
};

// Resolve the element_type label via i18n with a humanized fallback so
// unknown technical keys (e.g. a new CHAIN_CONDITION_V2) still render
// something readable instead of raw SNAKE_CASE.
export const resolveItemTitle = (item, t) => {
  if (!item) return '';
  if (item.element_type) {
    return t(`rapport.diagnostic.type.${item.element_type}`, {
      defaultValue: humanizeElementType(item.element_type),
    });
  }
  return t('rapport.diagnostic.item.unknown');
};

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${(p) => badgePalette(p.theme, p.status).bg};
  color: ${(p) => badgePalette(p.theme, p.status).color};
  border: 1px solid ${(p) => badgePalette(p.theme, p.status).border};
`;

const PhotoThumbnail = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.colors.gray.light};
`;

// Compact item card — single row by default (title left, badge right).
// Comment + photos expand below only when present, keeping non-problematic
// items short. Recommendations are rendered by the parent (Rapport.jsx)
// in a nested block so the item stays atomic.
const RapportDiagnosticItem = ({ item }) => {
  const { t } = useTranslation();
  const status = mapElementValueToStatus(item.element_value);
  const photos = Array.isArray(item.photos) ? item.photos.slice(0, 5) : [];
  const title = resolveItemTitle(item, t);
  const hasExpansion = !!item.comment || photos.length > 0;

  return (
    <Box
      as="article"
      data-testid="rapport-diagnostic-item"
      bg="white"
      borderRadius="xxs"
      px={3}
      py={hasExpansion ? 3 : 2}
      mb={2}
      border="1px solid"
      borderColor="gray.lighter">
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        style={{ gap: 12 }}>
        <Text mb={0} fontWeight={500} color="black">
          {title}
        </Text>
        {status && (
          <StatusBadge status={status} data-testid={`status-badge-${status}`}>
            {t(`rapport.diagnostic.status.${status}`, {
              defaultValue: status,
            })}
          </StatusBadge>
        )}
      </Box>

      {item.comment && (
        <Text mt={2} mb={0} color="gray.darker" fontSize="sm">
          {item.comment}
        </Text>
      )}

      {photos.length > 0 && (
        <Box
          flexDirection="row"
          flexWrap="wrap"
          mt={2}
          style={{ gap: 6 }}
          data-testid="rapport-diagnostic-photos">
          {photos.map((url, idx) => (
            <a
              key={url || idx}
              href={url}
              target="_blank"
              rel="noopener noreferrer">
              <PhotoThumbnail
                src={url}
                alt={t('rapport.diagnostic.photo.alt', {
                  index: idx + 1,
                  item: title,
                })}
                loading="lazy"
              />
            </a>
          ))}
        </Box>
      )}
    </Box>
  );
};

RapportDiagnosticItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    velo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    element_type: PropTypes.string,
    element_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    comment: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default RapportDiagnosticItem;
